import React, { useState, useEffect } from 'react';
import EmployeeApi from './employeeApi';
import { Employee } from './employeeType';
import { fetchEmployeeType } from '../reference/referenceApi';
import { fetchEmployeeTitle } from '../reference/referenceApi';
import { useNavigate } from 'react-router-dom';
import { getSessionData } from '../lib/global_function';
import HorizontalMenu from '../components/common/horizontalMenu';
import Breadcrumbs from '../components/common/breadcrumbs';
import { FilePlus } from 'lucide-react';
import { validateEmail, validateAndFormatPhoneNumber } from '../lib/validator';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select } from '../components/ui/select';
import { Alert } from '../components/ui/alert';

const CreateEmployeePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
    { label: 'Employee', path: '/main/company/employee' },
    { label: 'Create', path: '/main/company/create' },
  ];

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState<string[]>([]);
  const [employeeTitleOptions, setEmployeeTitleOptions] = useState<string[]>([]);

  const navigate = useNavigate();

  const [employee, setEmployee] = useState<Partial<Employee>>({
    first_name: '',
    last_name: '',
    company_name: '',
    employee_type: '',
    employee_title: '',
    phone: '',
    email: '',
    start_date: '',
    end_date: '',
    created_by_account_id: '',
  });

  // Fetch account data from sessionStorage
  useEffect(() => {
    const sessionData = getSessionData(process.env.REACT_APP_SESSION_STORAGE_KEY as string);
    const companyName = String(sessionData?.company_name);
    const accountId = String(sessionData?.account_id);

    if (typeof companyName === 'string' && companyName.trim() && typeof accountId === 'string' && accountId.trim()) {
      setErrorMessage(null);
      setEmployee((prevState) => ({
        ...prevState,
        company_name: companyName,
        created_by_account_id: accountId,
      }));
    } else {
      setErrorMessage('You must create a company first');
    }
  }, []);

  useEffect(() => {
    const loadEmployeeType = async () => {
      const employeeTypeData = await fetchEmployeeType();
      setEmployeeTypeOptions(employeeTypeData);
    };
    loadEmployeeType();
  }, []);

  useEffect(() => {
    const loadEmployeeTitle = async () => {
      try {
        const employeeTitleData = await fetchEmployeeTitle();
        setEmployeeTitleOptions(employeeTitleData);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };

    loadEmployeeTitle();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEmployee((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (errorMessage) {
      return;
    }

    if (!validateForm()) {
      return;
    }
    setErrorMessage('');

    try {
      await EmployeeApi.create(employee as Employee);
      setMessage('Employee created successfully');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unknown error occurred.');
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let errors: string[] = [];

    switch (name) {
      case 'phone':
        const phoneResult = validateAndFormatPhoneNumber(value);
        if (!phoneResult.isValid) {
          errors.push(phoneResult.error || 'Invalid phone number');
        } else {
          setEmployee((prev) => ({ ...prev, phone: phoneResult.value }));
        }
        break;

      case 'email':
        const emailResult = validateEmail(value);
        if (!emailResult.isValid) {
          errors.push(emailResult.error || 'Invalid email address format');
        } else {
          setEmployee((prev) => ({ ...prev, email: emailResult.value }));
        }
        break;

      default:
        break;
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
    } else {
      setErrorMessage(null);
    }
  };

  const validateForm = (): boolean => {
    let errors: string[] = [];

    // Validate phone
    const phoneResult = validateAndFormatPhoneNumber(employee.phone || '');
    if (!phoneResult.isValid) {
      errors.push(phoneResult.error || 'Invalid phone number');
    }

    // Validate email
    const emailResult = validateEmail(employee.email || '');
    if (!emailResult.isValid) {
      errors.push(emailResult.error || 'Invalid email address');
    }

    if (errors.length > 0) {
      setErrorMessage(errors.join(', '));
      return false;
    }

    setErrorMessage(null);
    return true;
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container mx-auto max-w-7xl px-4 py-6">
        <Breadcrumbs items={breadcrumbs} />

        <h2 className="text-2xl font-semibold mb-6">Create Employee</h2>

        {message ? (
          <Alert variant="success">{message}</Alert>
        ) : (
           <div className="bg-white rounded-2xl border-2 border-muted p-6 shadow-card">
          <form onSubmit={handleSubmit} className="space-y-3 max-w-2xl">
            {errorMessage && <Alert variant="error">{errorMessage}</Alert>}

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="first_name">First Name:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Input
                type="text"
                id="first_name"
                name="first_name"
                value={employee.first_name || ''}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="last_name">Last Name:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Input
                type="text"
                id="last_name"
                name="last_name"
                value={employee.last_name || ''}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="employee_type">Employee Type:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Select
                id="employee_type"
                name="employee_type"
                value={employee.employee_type || ''}
                onChange={handleChange}
                required
              >
                <option value="">Select an Employee Type</option>
                {employeeTypeOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="employee_title">Employee Title:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Select
                id="employee_title"
                name="employee_title"
                value={employee.employee_title || ''}
                onChange={handleChange}
                required
              >
                <option value="">Select an Employee title</option>
                {employeeTitleOptions.map((option, index) => (
                  <option key={index} value={option}>
                    {option}
                  </option>
                ))}
              </Select>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="phone">Phone:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Input
                type="tel"
                id="phone"
                name="phone"
                value={employee.phone || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-1">
                <Label htmlFor="email">Email:</Label>
                <span className="text-red-600 font-semibold">*</span>
              </div>
              <Input
                type="email"
                id="email"
                name="email"
                value={employee.email || ''}
                onChange={handleChange}
                onBlur={handleBlur}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="start_date">Start Date:</Label>
              <Input
                type="date"
                id="start_date"
                name="start_date"
                value={employee.start_date || ''}
                onChange={handleChange}
              />
            </div>

            <div className="pt-2">
              <Button type="submit" size="medium">
                <FilePlus size={16} />
                Create Employee
              </Button>
            </div>
          </form>
          </div>
        )}
      </div>
    </>
  );
};

export default CreateEmployeePage;